/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {AnalyticsIds} from '@ion/core/src/analytics/types';
import AvatarButton from '@ion/dls/src/components/buttons/AvatarButton';
import {HelpCenterButton} from '@ion/dls/src/components/buttons/HelpCenterButton';
import NotificationButton from '@ion/dls/src/components/buttons/NotificationButton';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import Render from '@ion/dls/src/render/Render';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {useCurrentUser} from '@ion/query/src/hooks/useCurrentUser';
import DesktopUserProfileView from './DesktopUserProfileView';
import GlobalAccountChooserDrawer from './GlobalAccountChooserDrawer';
import JobStatusToggle from './JobStatusToggle';
import useJobNotificationsPanel from './useJobNotificationsPanel';
import styles from './DesktopHeader.module.scss';

const useStyles = makeStyles(theme => ({
  avatarWrapper : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center',
    padding        : '0 2px',
    cursor         : 'pointer'
  },
  user : {
    display    : 'block',
    fontSize   : '16px',
    lineHeight : '20px',
    color      : dlsColor('grey1'),
    fontWeight : 500
  },
  options : {
    fontSize   : '14px',
    lineHeight : '20px',
    color      : dlsColor('grey2'),
    fontWeight : 400,
    cursor     : 'pointer'
  },
  account : {
    lineHeight : '20px',
    fontSize   : '14px',
    color      : dlsColor('grey3'),
    fontWeight : 400
  }
}));

export const IconCustomTooltip = function(props) {
  return <Tooltip arrow {...props} />;
};

/**
 * @DesktopHeaderContent
 *
 * The Desktop Header Notification component provides the application Header
 * Notification for the iOn Application when viewing in Desktop Mode.
 */

export const DesktopHeaderContent = () => {
  const {currentUser: {username} = {}}            = useCurrentUser();
  const {show, unreadCount, toggle}               = useJobNotificationsPanel();
  const [isUserProfileView, setIsUserProfileView] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = useCallback(function(event: React.MouseEvent<HTMLElement>) {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    setIsUserProfileView(!isUserProfileView);
  }, [anchorEl, isUserProfileView]);

  const open = Boolean(anchorEl) && isUserProfileView;
  const id = open ? 'simple-popover' : undefined;

  return (
    <div className={styles.icons} style={{flex: 1}}>
      <Render.If condition={show}>
        <NotificationButton notificationCount={unreadCount}
          toggleNotificationPanel={toggle} />
      </Render.If>
      <JobStatusToggle />
      <GlobalAccountChooserDrawer />
      <HelpCenterButton elementId={AnalyticsIds.helpCenterIcon} resourceCenterMenu/>
      <AvatarButton username={username} handleClick={handleClick} />
      {isUserProfileView &&
        <DesktopUserProfileView open={open}
          id={id}
          anchorEl={anchorEl}
          handleClick={handleClick}/>
      }
    </div>
  );
};

export default React.memo(DesktopHeaderContent);
